// AiFeatureConfigView — admin global escolhe qual provider+model+key a
// plataforma usa por feature key, quando o aluno NÃO tem chave própria.
//
// Lista as features conhecidas (com card explicativo) e mostra a config
// salva ou "usando fallback env" se nada estiver salvo. Permite editar
// inline (provider/model/apiKey/notes).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Brain,
  Check,
  FileAudio,
  FileUp,
  GraduationCap,
  Loader2,
  Mic,
  Save,
  Sparkles,
  Tag,
  Trash2
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, call, type AiFeatureConfigRow } from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface FeatureDef {
  key: string;
  label: string;
  description: string;
  icon: typeof Brain;
  /** Modelos sugeridos por provider — admin pode digitar qualquer string. */
  suggestedModels: { provider: string; models: string[] }[];
  defaultProvider: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
  defaultModel: string;
}

const FEATURES: FeatureDef[] = [
  {
    key: "lecture_transcribe",
    label: "Transcrição do Gravador de Aulas",
    description:
      "Áudio → texto. Modelo precisa aceitar áudio como input (Gemini Flash com audio, GPT-4o-mini-transcribe etc).",
    icon: Mic,
    suggestedModels: [
      { provider: "gemini", models: ["gemini-2.5-flash", "gemini-2.5-pro"] },
      { provider: "openai", models: ["gpt-4o-mini-transcribe", "gpt-4o-transcribe"] }
    ],
    defaultProvider: "gemini",
    defaultModel: "gemini-2.5-flash"
  },
  {
    key: "lecture_summary",
    label: "Resumo do Gravador de Aulas",
    description: "Transcrição → resumo detalhado/executivo/glossário/questões.",
    icon: FileAudio,
    suggestedModels: [
      { provider: "gemini", models: ["gemini-2.5-flash", "gemini-2.5-pro"] },
      { provider: "openai", models: ["gpt-4o-mini", "gpt-4o"] },
      { provider: "anthropic", models: ["claude-3-5-sonnet-20241022", "claude-3-haiku-20240307"] }
    ],
    defaultProvider: "gemini",
    defaultModel: "gemini-2.5-flash"
  },
  {
    key: "study_assistant",
    label: "Assistente IA no Modo Estudo",
    description:
      "Chat lado a lado das questões — explica conceitos, tira dúvidas. Volume alto, sensível a custo.",
    icon: Brain,
    suggestedModels: [
      { provider: "gemini", models: ["gemini-2.5-flash"] },
      { provider: "openai", models: ["gpt-4o-mini"] }
    ],
    defaultProvider: "gemini",
    defaultModel: "gemini-2.5-flash"
  },
  {
    key: "essay_grader",
    label: "Corretor de redação",
    description:
      "Avalia redação do aluno. Modelo precisa ser bom em texto longo e crítica construtiva.",
    icon: GraduationCap,
    suggestedModels: [
      { provider: "gemini", models: ["gemini-2.5-flash", "gemini-2.5-pro"] },
      { provider: "openai", models: ["gpt-4o-mini", "gpt-4o"] }
    ],
    defaultProvider: "gemini",
    defaultModel: "gemini-2.5-flash"
  },
  {
    key: "bank_ingest",
    label: "Upload de bancos via IA",
    description:
      "Aluno/professor manda PDF/DOCX/colado → IA estrutura em questões JSON. Precisa de modelo com bom file/long-context (PDF). Recomendado: gpt-4o ou gemini-2.5-pro.",
    icon: FileUp,
    suggestedModels: [
      { provider: "openai", models: ["gpt-4o", "gpt-4o-mini"] },
      { provider: "gemini", models: ["gemini-2.5-pro", "gemini-2.5-flash"] },
      {
        provider: "anthropic",
        models: ["claude-3-5-sonnet-20241022", "claude-3-haiku-20240307"]
      }
    ],
    defaultProvider: "openai",
    defaultModel: "gpt-4o"
  },
  {
    key: "bank_tagging",
    label: "Tags automáticas em bancos",
    description:
      "Categoriza questões em hierarquia granular (Cinemática → MRU; Literatura barroca; Química inorgânica). Pode reusar a mesma key do bank_ingest se quiser.",
    icon: Tag,
    suggestedModels: [
      { provider: "openai", models: ["gpt-4o-mini", "gpt-4o"] },
      { provider: "gemini", models: ["gemini-2.5-flash"] }
    ],
    defaultProvider: "openai",
    defaultModel: "gpt-4o-mini"
  }
];

const phase = signal<"loading" | "ready" | "error">("loading");
const configs = signal<AiFeatureConfigRow[]>([]);
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listAiFeatureConfigs();
    configs.value = r.configs;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function AiFeatureConfigView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  const byKey = new Map(configs.value.map((c) => [c.featureKey, c]));

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <Sparkles className="mr-1 inline size-4 text-violet-600" />
        <strong>IA por feature.</strong> Quando o aluno NÃO tem chave própria
        (BYOK), a plataforma usa estas configurações pra rodar a feature.
        Modelo, provider e chave são por-feature — assim você pode usar um
        modelo mais robusto onde faz diferença (transcrição, redação) e um
        mais barato no resto (chat, estudo).
      </div>

      <AiBudgetAlertSection />

      <div class="space-y-3">
        {FEATURES.map((f) => (
          <FeatureRow
            key={f.key}
            def={f}
            existing={byKey.get(f.key)}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Alerta de consumo de IA mensal — admin configura threshold + email destino
// =============================================================================

interface AiAlertsResponse {
  config: {
    enabled: boolean;
    thresholdCents: number;
    email: string | null;
    lastAlertSentAt: string | null;
    lastAlertPeriodKey: string | null;
  };
  currentMonth: {
    usageCents: number;
    calls: number;
    tokens: number;
    periodKey: string;
  };
}

function AiBudgetAlertSection() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [data, setData] = useState<AiAlertsResponse | null>(null);
  const [draftThresholdUsd, setDraftThresholdUsd] = useState<string>("20");
  const [draftEmail, setDraftEmail] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  async function load() {
    setPhase("loading");
    try {
      const r = await call<AiAlertsResponse>("/api/manager/ai-alerts-config");
      setData(r);
      setDraftThresholdUsd((r.config.thresholdCents / 100).toFixed(2));
      setDraftEmail(r.config.email ?? "");
      setEnabled(r.config.enabled);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save() {
    setSaving(true);
    setSavedMsg(null);
    try {
      const usdNum = parseFloat(draftThresholdUsd.replace(",", "."));
      if (Number.isNaN(usdNum) || usdNum <= 0) {
        alert("Threshold inválido. Use um número maior que 0.");
        setSaving(false);
        return;
      }
      await call("/api/manager/ai-alerts-config", {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          thresholdCents: Math.round(usdNum * 100),
          email: draftEmail.trim() || null
        })
      });
      setSavedMsg("Salvo ✓");
      void load();
      setTimeout(() => setSavedMsg(null), 2000);
    } catch (e) {
      setSavedMsg(`Erro: ${friendlyError(e)}`);
    } finally {
      setSaving(false);
    }
  }

  if (phase === "loading") {
    return <Skeleton className="h-40 w-full" />;
  }
  if (phase === "error" || !data) {
    return null;
  }

  const usageUsd = (data.currentMonth.usageCents / 100).toFixed(2);
  const usageBrl = ((data.currentMonth.usageCents / 100) * 5.2).toFixed(2);
  const thresholdUsd = (data.config.thresholdCents / 100).toFixed(2);
  const pct = data.config.thresholdCents > 0
    ? Math.min(100, Math.round((data.currentMonth.usageCents / data.config.thresholdCents) * 100))
    : 0;
  const alreadyAlerted = data.config.lastAlertPeriodKey === data.currentMonth.periodKey;

  return (
    <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div class="flex-1">
          <h3 class="text-sm font-bold">Alerta de consumo de IA</h3>
          <p class="text-xs text-muted-foreground">
            Quando o consumo platform-only ultrapassar o threshold no mês,
            mandamos email pro admin (1× por mês). BYOK não conta.
          </p>
        </div>
      </div>

      {/* Barra de consumo do mês */}
      <div class="rounded-md bg-background p-3">
        <div class="mb-1.5 flex items-center justify-between text-xs">
          <span class="font-semibold">
            Mês atual ({data.currentMonth.periodKey})
          </span>
          <span class="text-muted-foreground">
            <strong class="text-foreground">USD {usageUsd}</strong> / USD {thresholdUsd}
            <span class="ml-1.5 opacity-70">≈ R$ {usageBrl}</span>
          </span>
        </div>
        <div class="h-2 overflow-hidden rounded-full bg-muted">
          <div
            class={`h-full transition-all ${pct >= 100 ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div class="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {data.currentMonth.calls} chamadas · {data.currentMonth.tokens.toLocaleString()} tokens
          </span>
          <span>{pct}%</span>
        </div>
        {alreadyAlerted && (
          <div class="mt-2 inline-flex items-center gap-1 rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
            ✓ Alerta deste mês já foi enviado em{" "}
            {data.config.lastAlertSentAt &&
              new Date(data.config.lastAlertSentAt).toLocaleString("pt-BR")}
          </div>
        )}
      </div>

      {/* Configurações editáveis */}
      <div class="grid gap-3 md:grid-cols-3">
        <label class="block">
          <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Threshold (USD/mês)
          </div>
          <input
            type="number"
            step="0.01"
            min="1"
            value={draftThresholdUsd}
            onInput={(e) => setDraftThresholdUsd((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          />
          <div class="mt-1 text-[10px] text-muted-foreground">
            Equivale a R$ {(parseFloat(draftThresholdUsd.replace(",", ".")) * 5.2).toFixed(2)} aproximadamente
          </div>
        </label>
        <label class="block md:col-span-2">
          <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Email destinatário (deixe vazio pra usar seu email de admin)
          </div>
          <input
            type="email"
            value={draftEmail}
            onInput={(e) => setDraftEmail((e.target as HTMLInputElement).value)}
            placeholder="seu@email.com"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          />
        </label>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2">
        <label class="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled((e.target as HTMLInputElement).checked)}
          />
          <span>Alerta ativo</span>
        </label>
        <div class="flex items-center gap-2">
          {savedMsg && (
            <span class="text-[11px] text-emerald-600 dark:text-emerald-400">
              {savedMsg}
            </span>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : null}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({
  def,
  existing
}: {
  def: FeatureDef;
  existing: AiFeatureConfigRow | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState<
    "gemini" | "openai" | "anthropic" | "xai" | "deepseek"
  >(
    (existing?.provider as any) ?? def.defaultProvider
  );
  const [model, setModel] = useState(existing?.model ?? def.defaultModel);
  const [apiKey, setApiKey] = useState("");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [busy, setBusy] = useState(false);

  const Icon = def.icon;
  const isConfigured = !!existing?.apiKeySet;

  async function save() {
    if (!apiKey.trim() && !existing?.apiKeySet) {
      void openAlert({
        title: "Chave obrigatória",
        message: "Cole a API Key na primeira configuração desta feature."
      });
      return;
    }
    setBusy(true);
    try {
      await api.upsertAiFeatureConfig({
        featureKey: def.key,
        provider,
        model: model.trim(),
        apiKey: apiKey.trim() || undefined,
        notes: notes.trim() || undefined
      });
      setApiKey("");
      setEditing(false);
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao salvar",
        message: friendlyError(e)
      });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await openConfirm({
      title: "Remover configuração?",
      message: `A feature "${def.label}" voltará pra fallback de env var (${def.defaultProvider}/${def.defaultModel}). Alunos sem BYOK podem ficar sem IA até você configurar de novo.`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    await api.deleteAiFeatureConfig(def.key);
    await load();
  }

  return (
    <div
      class={`rounded-md border p-4 transition-colors ${
        isConfigured
          ? "border-emerald-500/40 bg-emerald-500/5"
          : "border-border bg-card"
      }`}
    >
      <header class="flex items-start gap-3">
        <div
          class={`grid size-10 shrink-0 place-items-center rounded-md ${
            isConfigured
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-violet-500/15 text-violet-600"
          }`}
        >
          <Icon className="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <h3 class="flex items-center gap-2 text-base font-bold">
            {def.label}
            {isConfigured && (
              <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                <Check className="size-3" />
                configurado
              </span>
            )}
          </h3>
          <p class="mt-0.5 text-xs text-muted-foreground">{def.description}</p>
          <code class="mt-1 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            feature_key: {def.key}
          </code>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            class="shrink-0 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:border-primary hover:text-primary"
          >
            {isConfigured ? "Editar" : "Configurar"}
          </button>
        )}
      </header>

      {!editing && isConfigured && existing && (
        <div class="mt-3 grid gap-2 rounded-md border border-border bg-background/40 p-3 text-xs sm:grid-cols-2">
          <div>
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Provider
            </span>
            <div class="font-mono">{existing.provider}</div>
          </div>
          <div>
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Modelo
            </span>
            <div class="font-mono">{existing.model}</div>
          </div>
          {existing.notes && (
            <div class="sm:col-span-2">
              <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Notas
              </span>
              <div class="text-muted-foreground">{existing.notes}</div>
            </div>
          )}
        </div>
      )}

      {!editing && !isConfigured && (
        <div class="mt-3 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertCircle className="mr-1 inline size-3.5" />
          Sem config DB. Usando fallback de env var ({def.defaultProvider}/
          {def.defaultModel}). Recomenda-se configurar pra ter controle do
          custo + modelo apropriado.
        </div>
      )}

      {editing && (
        <div class="mt-3 space-y-3 rounded-md border border-primary/40 bg-primary/5 p-3">
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="block">
              <span class="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Provider
              </span>
              <select
                value={provider}
                onChange={(e) =>
                  setProvider((e.target as HTMLSelectElement).value as any)
                }
                aria-label="Provider"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="gemini">Gemini</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="xai">xAI</option>
                <option value="deepseek">DeepSeek</option>
              </select>
            </label>
            <label class="block">
              <span class="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Modelo
              </span>
              <input
                type="text"
                value={model}
                onInput={(e) => setModel((e.target as HTMLInputElement).value)}
                placeholder="ex: gemini-2.5-flash"
                title="Modelo"
                aria-label="Modelo"
                class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
              />
              <div class="mt-1 flex flex-wrap gap-1">
                {def.suggestedModels
                  .find((g) => g.provider === provider)
                  ?.models.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModel(m)}
                      class="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] hover:border-primary"
                    >
                      {m}
                    </button>
                  ))}
              </div>
            </label>
          </div>
          <label class="block">
            <span class="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              API Key{" "}
              {existing?.apiKeySet && (
                <span class="font-normal normal-case tracking-normal text-emerald-600 dark:text-emerald-400">
                  (uma já salva — deixe vazio pra manter)
                </span>
              )}
            </span>
            <input
              type="password"
              value={apiKey}
              onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
              placeholder={existing?.apiKeySet ? "•••••••• (mantém atual)" : "Cole a chave"}
              title="API Key"
              aria-label="API Key"
              class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
            />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Chave fica cifrada at-rest no DB (AES-256-GCM). Nunca devolvemos
              em claro no GET.
            </p>
          </label>
          <label class="block">
            <span class="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Notas (opcional)
            </span>
            <textarea
              value={notes}
              onInput={(e) =>
                setNotes((e.target as HTMLTextAreaElement).value)
              }
              placeholder="Ex: usando GPT-4o-mini pra balancear custo/qualidade do gravador"
              rows={2}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <div class="flex items-center justify-between gap-2">
            {isConfigured ? (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="size-3" />
                Remover config
              </button>
            ) : (
              <span />
            )}
            <div class="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setApiKey("");
                }}
                disabled={busy}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy || !model.trim()}
                class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Save className="size-3" />
                )}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
